import { Rect } from 'konva/lib/shapes/Rect';
import { Circle } from 'konva/lib/shapes/Circle';
import { KonvaEventObject, Node } from 'konva/lib/Node';
import { Group } from 'konva/lib/Group';
import KonvaService from '.';
import { EventType, emitter } from '@/utils/emitter.utils';
import { KonvaUtils } from './konva.utils';
import { Shape, ShapeType } from '../shape';

export class KonvaHover {
  private hoverStroke = 'rgba(13, 132, 242, 0.7)';
  private centerPointCircle: Circle | null = null;
  private currentCenterPointShapeName: string | null = null;
  /** 弧形进度条辅助线相关 */
  private arcGuideGroup: Group | null = null;
  private currentArcGuideShapeName: string | null = null;
  private konvaService: KonvaService;
  constructor(konvaService: KonvaService) {
    this.konvaService = konvaService;
    this.bindEvent();
  }

  
  /**
   * 递归计算节点在Layer坐标系中的绝对位置
   * 处理多层嵌套：rect -> Group -> Group -> ... -> Layer
   * @param node 起始节点
   * @returns 在Layer坐标系中的绝对位置
   */
  private getAbsolutePositionInLayer(node: Node): { x: number; y: number } {
    let position = node.position();
    let current = node.parent;

    // 递归向上遍历父节点，直到到达Layer
    while (current) {
      const nodeType = current.getType?.();
      // 如果是Layer，停止递归
      if (nodeType === 'Layer') {
        break;
      }
      // 如果是Group或其他容器，累加其位置
      // 累加所有非Layer的父节点位置（包括Group和其他容器）
      const parentPosition = current.position();
      position = {
        x: parentPosition.x + position.x,
        y: parentPosition.y + position.y
      };
      current = current.parent;
    }

    return position;
  }

  /**
   * 显示图片中心点
   * @param shapeName shape名称
   */
  private showCenterPoint(shapeName: string) {
    this.hideCenterPoint();


    const shape = Shape.getShape(shapeName);
    if (!shape || (shape.type !== ShapeType.Image)) {
      return;
    }

    // 关键：立即记录当前显示中心点的shape，确保refreshCenterPoint能正确恢复
    this.currentCenterPointShapeName = shapeName;

    // 获取图片在konva画布上的实际节点
    const rect = this.konvaService.getShapeNodes().find((item) => item.name() === shapeName);

    if (!rect) return;

    // 计算图片的几何中心位置（不是旋转中心点）
    // 获取节点在Layer坐标系中的绝对位置（递归处理多层嵌套）
    const position = this.getAbsolutePositionInLayer(rect);

    // 获取节点的 offset（旋转中心点相对于左上角的偏移）
    const offset = rect.offset();
    // 获取实际尺寸
    const actualSize = KonvaUtils.getImageActualSize(shape);
    // 获取旋转角度（弧度）
    const rotation = rect.rotation() * (Math.PI / 180);

    // 计算几何中心相对于旋转中心的偏移向量（未旋转坐标系中）
    const dx = actualSize.width / 2 - offset.x;
    const dy = actualSize.height / 2 - offset.y;

    // 将偏移向量根据旋转角度进行旋转
    const rotatedDx = dx * Math.cos(rotation) - dy * Math.sin(rotation);
    const rotatedDy = dx * Math.sin(rotation) + dy * Math.cos(rotation);

    // 几何中心 = 旋转中心 + 旋转后的偏移向量
    const centerX = position.x + rotatedDx;
    const centerY = position.y + rotatedDy;

    this.centerPointCircle = new Circle({
      x: centerX,
      y: centerY,
      radius: 4,
      fill: '#FF0000',
      id: 'center-point',
      listening: false,
      opacity: 1,
      visible: true
    });

    this.konvaService.layer?.add(this.centerPointCircle);
    this.konvaService.layer?.batchDraw();
  }

  /**
   * 更新中心点位置
   * @param shapeName shape名称
   */
  public updateCenterPoint(shapeName: string) {
    if (!this.centerPointCircle) return;

    const shape = Shape.getShape(shapeName);
    if (!shape || (shape.type !== ShapeType.Image)) {
      return;
    }

    // 获取图片在konva画布上的实际节点
    const rect = this.konvaService.getShapeNodes().find((item) => item.name() === shapeName);

    if (!rect) return;

    // 计算图片的几何中心位置（不是旋转中心点）
    // 获取节点在Layer坐标系中的绝对位置（递归处理多层嵌套）
    const position = this.getAbsolutePositionInLayer(rect);
    // 获取节点的 offset（旋转中心点相对于左上角的偏移）
    const offset = rect.offset();
    // 获取实际尺寸
    const actualSize = KonvaUtils.getImageActualSize(shape);
    // 获取旋转角度（弧度）
    const rotation = rect.rotation() * (Math.PI / 180);

    // 计算几何中心相对于旋转中心的偏移向量（未旋转坐标系中）
    const dx = actualSize.width / 2 - offset.x;
    const dy = actualSize.height / 2 - offset.y;

    // 将偏移向量根据旋转角度进行旋转
    const rotatedDx = dx * Math.cos(rotation) - dy * Math.sin(rotation);
    const rotatedDy = dx * Math.sin(rotation) + dy * Math.cos(rotation);

    // 几何中心 = 旋转中心 + 旋转后的偏移向量
    const centerX = position.x + rotatedDx;
    const centerY = position.y + rotatedDy;

    this.centerPointCircle.position({
      x: centerX,
      y: centerY
    });
    this.konvaService.layer?.batchDraw();
  }

  /**
   * 隐藏图片中心点
   */
  private hideCenterPoint() {
    if (this.centerPointCircle) {
      this.centerPointCircle.destroy();
      this.centerPointCircle = null;
      this.currentCenterPointShapeName = null;
      this.konvaService.layer?.draw();
    }
  }

  /**
   * 显示弧形进度条辅助线（旋转中心点 + 外径圆 + 内径圆）
   */
  private showArcGuide(shapeName: string) {
    this.hideArcGuide();

    const shape = Shape.getShape(shapeName);
    if (!shape || shape.type !== ShapeType.ProgressBar) return;
    if (shape.attrs.progressBarType !== 'arc') return;

    const { barRadius, barWidth, pivotX, pivotY } = shape.attrs;
    if (barRadius == null || barWidth == null) return;

    // pivotX/pivotY 未设置时，默认取元素中心（与导出逻辑 progressBar.ts 一致）
    const { w = 0, h = 0 } = shape.attrs;
    const effectivePivotX = pivotX ?? w / 2;
    const effectivePivotY = pivotY ?? h / 2;

    this.currentArcGuideShapeName = shapeName;

    // 获取 Konva 节点在 Layer 坐标系中的绝对位置
    // 优先查找 ProgressBar 自身的节点，成组时也能找到（它在 Group 内部）
    const allNodes = this.konvaService.layer?.find('#shape') ?? [];
    const rect = allNodes.find((item) => item.name() === shapeName);
    if (!rect) return;

    const position = this.getAbsolutePositionInLayer(rect);

    // 弧形进度条的旋转中心在元素坐标系中的位置是 (pivotX, pivotY)
    const centerX = position.x + effectivePivotX;
    const centerY = position.y + effectivePivotY;

    const outerRadius = barRadius + barWidth / 2;
    const innerRadius = Math.max(0, barRadius - barWidth / 2);

    const guideColor = 'rgba(255, 80, 80, 0.6)';
    const dashPattern = [4, 4];

    this.arcGuideGroup = new Group({
      listening: false
    });

    // 旋转中心点（红色实心圆）
    this.arcGuideGroup.add(
      new Circle({
        x: centerX,
        y: centerY,
        radius: 3,
        fill: '#FF3333',
        listening: false
      })
    );

    // 外径圆（虚线）
    this.arcGuideGroup.add(
      new Circle({
        x: centerX,
        y: centerY,
        radius: outerRadius,
        stroke: guideColor,
        strokeWidth: 0.8,
        dash: dashPattern,
        listening: false
      })
    );

    // 内径圆（虚线）
    if (innerRadius > 0) {
      this.arcGuideGroup.add(
        new Circle({
          x: centerX,
          y: centerY,
          radius: innerRadius,
          stroke: guideColor,
          strokeWidth: 0.8,
          dash: dashPattern,
          listening: false
        })
      );
    }

    this.konvaService.layer?.add(this.arcGuideGroup);
    this.konvaService.layer?.batchDraw();
  }

  /**
   * 隐藏弧形进度条辅助线
   */
  private hideArcGuide() {
    if (this.arcGuideGroup) {
      this.arcGuideGroup.destroy();
      this.arcGuideGroup = null;
      this.currentArcGuideShapeName = null;
      this.konvaService.layer?.draw();
    }
  }

  /**
   * 刷新中心点位置
   * 在重绘shapes后调用，如果中心点存在则重新添加到新的layer
   */
  public refreshCenterPoint() {
    // 由于drawShapes会清空并重建layer，centerPointCircle会被销毁
    // 如果之前显示了中心点，需要重新创建并显示
    if (this.currentCenterPointShapeName) {
      const shapeName = this.currentCenterPointShapeName;
      this.centerPointCircle = null; // 重置以便重新创建
      this.showCenterPoint(shapeName);
    }
    // 同样刷新弧形进度条辅助线
    if (this.currentArcGuideShapeName) {
      const shapeName = this.currentArcGuideShapeName;
      this.arcGuideGroup = null;
      this.showArcGuide(shapeName);
    }
  }

  /**
   * 获取当前显示中心点的 shape 名称
   */
  public getCurrentCenterPointShapeName(): string | null {
    return this.currentCenterPointShapeName;
  }

  /**
   * 设置当前显示中心点的 shape 名称
   */
  public setCurrentCenterPointShapeName(shapeName: string | null) {
    this.currentCenterPointShapeName = shapeName;
  }

  /**
   * 显示或隐藏shape hover状态
   * @param target {name,rect}
   * @param hover 显示或隐藏
   * @param centerPointShapeName 可选，用于显示中心点的shape名称（成组时使用原始子图片名称）
   * @returns
   */
  toggleHover(target: string | Rect, hover: boolean, centerPointShapeName?: string, arcGuideShapeName?: string) {
    const rect =
      typeof target == 'string'
        ? (this.konvaService
            .getShapeNodes()
            .find((item) => item.name() == target) as Rect)
        : target;
    if (!rect) return;

    // clear all
    this.konvaService.getShapeNodes().forEach((node) => {
      (<Rect>node).strokeWidth?.(0);
    });
    // 不可见元素不可hover
    if (!rect.visible()) {
      return;
    }

    if (this.konvaService.isTransforming) {
      return;
    }
    // 然后再重新设置状态
    if (hover) {
      rect.stroke?.(this.hoverStroke);
      rect.strokeWidth?.(hover ? 0.8 : 0);
      // 显示图片中心点 - 优先使用传入的centerPointShapeName（用于成组场景）
      const shapeNameForCenter = centerPointShapeName || rect.name();
      this.showCenterPoint(shapeNameForCenter);
      // 显示弧形进度条辅助线 - 优先使用传入的arcGuideShapeName（用于成组场景）
      const shapeNameForArcGuide = arcGuideShapeName || rect.name();
      this.showArcGuide(shapeNameForArcGuide);
    } else {
      // 隐藏图片中心点
      this.hideCenterPoint();
      // 隐藏弧形进度条辅助线
      this.hideArcGuide();
    }

    this.konvaService.layer?.draw();
  }

  private bindEvent() {
    this.konvaService.layer?.on('mousemove', (e) => {
      // 此时node有可能是Rect或者Transfotmer
      // 仅监听layer上的rect的移动
      const node = e.target as Node;
      if (node?.id() !== 'shape') return;

      // clear已有的选择
      this.konvaService
        .getShapeNodes()
        .filter((item) => item !== node)
        .forEach((node) => {
          (<Rect>node).strokeWidth?.(0);
        });
      emitter.emit(EventType['konva:shape-hover'], '');

      // 然后重新进行选择
      if (this.konvaService.isTransforming) {
        return;
      }

      // 获取可交互节点
      const _node = KonvaUtils.getInteractableNode(node);
      // console.log('hover', node, _node);

      // 不可选择禁用的
      if (!KonvaUtils.isNodeEnabled(_node)) return;

      // 如果当前为选中状态，则返回
      // if (this.konvaService.tr?.nodes().includes(_node)) return;

      // 对于成组的图片，传入原始node的name来显示中心点
      // 检查原始node是否是Image类型
      const originalShape = Shape.getShape(node.name());
      const centerPointShapeName = originalShape?.type === ShapeType.Image ? node.name() : undefined;
      // 对于成组的弧形进度条，传入原始node的name来显示辅助线
      const arcGuideShapeName = originalShape?.type === ShapeType.ProgressBar ? node.name() : undefined;

      this.toggleHover(_node as Rect, true, centerPointShapeName, arcGuideShapeName);
      emitter.emit(EventType['konva:shape-hover'], _node.name());
    });

    this.konvaService.layer?.on('mouseleave mousedown', (e) => {
      function isContextmenu(e: KonvaEventObject<MouseEvent>) {
        const { ctrlKey, button } = e.evt;
        return button === 2 || (button == 0 && ctrlKey);
      }
      // 此时node有可能是Rect或者Transfotmer
      // 仅监听layer上的rect的移动
      const node = e.target as Node;
      if (node?.id() !== 'shape') return;
      if (isContextmenu(e)) return;

      this.toggleHover(node as Rect, false);
      this.hideCenterPoint();
      this.hideArcGuide();
      emitter.emit(EventType['konva:shape-hover'], '');
    });
  }
}
